import { getEntities, getEntityMap } from "./selectorUtils";
import { createSelector } from "@reduxjs/toolkit";
import { getPlatforms as getAccountPlatforms } from "utils/accounts";
import { postsContentOptions } from "constants/PostsFliters";
import { SelectorRootState } from "./types";
import type { LoadedDraft, Post } from "shared/types/post";

const POSTS_ENTITY = "posts";

export const getPageInitialized = (state: SelectorRootState) =>
  state.postsView.initialized;

export const getExpanded = (state: SelectorRootState) =>
  state.postsView.expanded;
export const getSidebar = (state: SelectorRootState) => state.postsView.sidebar;
export const getPlatforms = (state: SelectorRootState) =>
  state.postsView.platforms;

export const getFilters = (state: SelectorRootState) => state.postsView.filters;
export const getStorePostContentTypes = (state: SelectorRootState) =>
  state.postsView.filters.postContentTypes;
export const getUpcomingFilters = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].filters;
export const getPostsFilter = (state: SelectorRootState) =>
  state.postsView.postsFilter;
export const getTotalPosts = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].total;
export const getHasMore = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].hasMore;
export const getIsLoadingMore = (state: SelectorRootState) =>
  Boolean(state.entities[POSTS_ENTITY].loadingMore);
export const getPostsError = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].error;
export const getSelectedPostIds = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].selectedIds;
export const getSelectAll = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].allSelected;

// Accounts
export const getSelectedAccounts = (state: SelectorRootState) =>
  state.entities.accounts.selectedIds;
export const getSelectedAccountsEntity = createSelector(
  (state: SelectorRootState) => state.entities.accounts,
  (entities) =>
    entities.selectedIds.map((id) => entities.byId[id]).filter(Boolean)
);
export const getAccounts = createSelector(
  (state: SelectorRootState) => state.entities.accounts,
  (entities) => entities.allIds.map((id) => entities.byId[id]).filter(Boolean)
);

// Posts
export const getPosts = createSelector(
  (state: SelectorRootState) => state.entities[POSTS_ENTITY].allIds,
  (state: SelectorRootState) => state.entities[POSTS_ENTITY].byId,
  (allIds, byId) => {
    return getEntityMap(allIds, byId);
  }
);
export const getPostIds = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].allIds;
export const getLoadedPostCount = (state: SelectorRootState) =>
  state.entities[POSTS_ENTITY].allIds.length;
export const getSelectedPosts = (state: SelectorRootState) =>
  getEntities<Post | LoadedDraft>(
    state,
    POSTS_ENTITY,
    getSelectedPostIds(state)
  );

// Post - Edit Mode
export const getEdittingPostIds = (state: SelectorRootState) =>
  state.forms.postForms.allIds;

// Posts old
export const getNumSelected = (state: SelectorRootState) =>
  state.postsView.selectedPostIds.length;
export const getPostCount = (state: SelectorRootState) =>
  state.entities.posts.allIds.length;

// Pagination
export const getPagination = (state: SelectorRootState) => state.pagination;
export const getPages = (state: SelectorRootState) =>
  getPagination(state).pages;
export const getCurrentPage = (state: SelectorRootState) =>
  getPagination(state).currentPage;
export const getLastPage = (state: SelectorRootState) =>
  getPagination(state).lastPage;
export const getPageSize = (state: SelectorRootState) =>
  getPagination(state).pageSize;

export const getPostContentTypes = createSelector(
  getStorePostContentTypes,
  getSelectedAccountsEntity,
  (statePostContentTypes, selectedAccountsEntity) => {
    const platformsTypes = getAccountPlatforms(selectedAccountsEntity).map(
      ({ TYPE }) => TYPE
    );
    const postContentTypes = postsContentOptions
      .filter((option) => {
        return (
          statePostContentTypes.includes(option.value) &&
          option.relatedTo?.some((platform) =>
            platformsTypes.includes(platform as (typeof platformsTypes)[number])
          )
        );
      })
      .map((option) => option.value);

    if (statePostContentTypes.includes("*")) {
      postContentTypes.push("*");
    }

    return postContentTypes;
  }
);
